import { useEffect, useState, createContext, useContext, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import HomePage from "@/pages/HomePage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminDashboard from "@/pages/AdminDashboard";

// Components
import AlertSystem from "@/components/alerts/AlertSystem";

// Backend API URL - uses env variable, falls back to relative path
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('grindtime_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('grindtime_token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (e) {
          localStorage.removeItem('grindtime_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('grindtime_token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, referral_code = null) => {
    const payload = { email, password, name };
    if (referral_code) {
      payload.referral_code = referral_code;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem('grindtime_token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('grindtime_token');
    setToken(null);
    setUser(null);
  };

  // Create authAxios instance that updates when token changes
  const authAxios = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
    });
    
    // Add request interceptor to always use the latest token
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('grindtime_token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    
    return instance;
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, authAxios, API }}>
      <div className="App min-h-screen bg-void-black">
        <Toaster position="top-right" theme="dark" />
        <AlertSystem />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
